import useAuth from "@/lib/auth/useAuth";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import { Pump, SystemType } from "@/types/pump";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { InferType, number, object, string } from "yup";
import { useQueryClient } from "@tanstack/react-query";
import SelectButtonInput from "@/components/inputs/SelectButtonInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@heroui/react";
import { faArrowLeft, faSave } from "@fortawesome/free-solid-svg-icons";
import SelectInput from "@/components/inputs/SelectInput";
import TextInput from "@/components/inputs/TextInput";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";

const schema = object().shape({
    min_hertz: number().when("system_type", {
        is: (system_type: number) => system_type == SystemType.variableRate,
        then: () => number().required(),
        otherwise: () => number().notRequired(),
    }),
    max_hertz: number().when("system_type", {
        is: (system_type: number) => system_type == SystemType.variableRate,
        then: () => number().required(),
        otherwise: () => number().notRequired(),
    }),
    min_injection_rate: number().when("system_type", {
        is: (system_type: number) => system_type == SystemType.variableRate,
        then: () => number().required(),
        otherwise: () => number().notRequired(),
    }),
    max_injection_rate: number().required(),
    pump_make: string().required(),
    pump_model: string().when("system_type", {
        is: (system_type: number) => system_type == SystemType.variableRate,
        then: () => string().required(),
        otherwise: () => string().notRequired(),
    }),
    pump_serial_number: string().nullable(),
    system_type: number().required(),
});

const MODELS = [
    "Reflex Connect",
    "Reflex Evolution",
    "Standard",
    "Insectigator",
    "EZE-Grab",
    "E-Z Meter",
    "Marksman",
];

type FormType = InferType<typeof schema>;

function PumpForm({
    editingPump,
    onFinish,
}: {
    editingPump?: Pump;
    onFinish?: () => void;
}) {
    const { formatUnit } = useI18n();
    const { t } = useTranslation("messages");
    const { useGetRequest, useMutateRequest } = useRequest();
    const { fieldQuery } = useFieldDashboard() ?? {};
    const { user } = useAuth();

    const pumpBrandsQuery = useGetRequest<string[]>({
        url: "pumps/brands/",
    });

    const savePump = useMutateRequest({
        successMessage: t("success.pump_saved_successfully"),
        errorMessage: t("error.failed_to_save_pump"),
    });

    const f = useForm<FormType>({
        resolver: yupResolver(schema),
        defaultValues: {
            min_hertz: editingPump?.min_hertz,
            max_hertz: editingPump?.max_hertz,
            pump_make: editingPump?.pump_make,
            pump_model: editingPump?.pump_model,
            pump_serial_number: editingPump?.pump_serial_number,
            min_injection_rate: editingPump?.min_injection_rate,
            max_injection_rate: editingPump?.max_injection_rate,
            system_type: (editingPump?.system_type?.toString() ??
                SystemType.constantRate.toString()) as unknown as number,
        },
    });

    const SYSTEM_TYPES = [
        {
            id: 3,
            description: t("chemigation_systems.constant_rate", {
                ns: "setup",
            }),
        },
        {
            id: 2,
            description: t("chemigation_systems.variable_rate", {
                ns: "setup",
            }),
        },
    ];

    const queryClient = useQueryClient();

    const onSave = useCallback(
        (formData: FormType) => {
            const payload = {
                ...formData,
                monitor_system: null,
                grower: fieldQuery?.data?.grower,
                created_by: user?.id,
                last_modified_by: user?.id,
            };

            savePump.mutate(
                {
                    url: editingPump ? `pumps/${editingPump.id}/` : "pumps/",
                    method: editingPump ? "PUT" : "POST",
                    data: payload,
                },
                {
                    onSuccess: () => {
                        queryClient.invalidateQueries({
                            queryKey: ["pumps/"],
                            exact: true,
                            type: "active",
                        });
                        onFinish?.();
                    },
                },
            );
        },
        [
            fieldQuery?.data?.grower,
            user?.id,
            savePump,
            editingPump,
            queryClient,
            onFinish,
        ],
    );

    const [system_type] = f.watch(["system_type"]);

    return (
        <FormProvider {...f}>
            <form>
                <div className="font-semibold flex flex-row gap-2 items-center">
                    <Button
                        size="sm"
                        variant="light"
                        startContent={<FontAwesomeIcon icon={faArrowLeft} />}
                        onPress={onFinish}
                    >
                        {t("back", {
                            ns: "common",
                        })}
                    </Button>
                    {editingPump
                        ? t("chemigation_systems.edit_pump", { ns: "setup" })
                        : t("chemigation_systems.add_pump", { ns: "setup" })}
                </div>
                <div className="flex flex-col gap-2">
                    <SelectButtonInput
                        name="system_type"
                        label={t("chemigation_systems.system_type", {
                            ns: "setup",
                        })}
                        options={SYSTEM_TYPES}
                        orientation="horizontal"
                        isRequired
                        size="sm"
                        classNames={{
                            base: "flex-1 w-full",
                        }}
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <SelectInput
                            name="pump_make"
                            label={t("chemigation_systems.pump_brand", {
                                ns: "setup",
                            })}
                            labelPlacement="outside"
                            size="sm"
                            isRequired
                            placeholder="Select Pump Brand"
                            options={
                                pumpBrandsQuery.data?.map((brand) => ({
                                    id: brand,
                                    description: brand,
                                })) ?? []
                            }
                            hideErrorMessage
                        />
                        {system_type == SystemType.variableRate && (
                            <>
                                <SelectInput
                                    name="pump_model"
                                    label={t(
                                        "chemigation_systems.rate_controller",
                                        {
                                            ns: "setup",
                                        },
                                    )}
                                    isRequired
                                    labelPlacement="outside"
                                    size="sm"
                                    placeholder="Select Pump Model"
                                    options={MODELS.map((model) => ({
                                        id: model,
                                        description: model,
                                    }))}
                                    hideErrorMessage
                                />
                                <TextInput
                                    className="col-span-2"
                                    name="pump_serial_number"
                                    label={t(
                                        "chemigation_systems.pump_serial_number",
                                        {
                                            ns: "setup",
                                        },
                                    )}
                                    labelPlacement="outside"
                                    placeholder="Serial Number"
                                    size="sm"
                                    hideErrorMessage
                                />
                                <TextInput
                                    name="min_hertz"
                                    label={t("chemigation_systems.min_hertz", {
                                        ns: "setup",
                                    })}
                                    labelPlacement="outside"
                                    placeholder="Min Hz"
                                    size="sm"
                                    type="number"
                                    min={0}
                                    hideErrorMessage
                                    isRequired
                                />
                                <TextInput
                                    hideErrorMessage
                                    name="max_hertz"
                                    label={t("chemigation_systems.max_hertz", {
                                        ns: "setup",
                                    })}
                                    labelPlacement="outside"
                                    placeholder="Max Hz"
                                    size="sm"
                                    type="number"
                                    min={0}
                                    isRequired
                                />
                                <TextInput
                                    hideErrorMessage
                                    name="min_injection_rate"
                                    label={`${t(
                                        "chemigation_systems.min_injection_rate",
                                        {
                                            ns: "setup",
                                        },
                                    )} (${formatUnit("gph", "Lph")})`}
                                    labelPlacement="outside"
                                    placeholder="Min Injection Rate"
                                    size="sm"
                                    type="number"
                                    min={0}
                                    isRequired
                                />
                            </>
                        )}
                        <TextInput
                            hideErrorMessage
                            name="max_injection_rate"
                            label={`${t(
                                "chemigation_systems.max_injection_rate",
                                {
                                    ns: "setup",
                                },
                            )} (${formatUnit("gph", "Lph")})`}
                            labelPlacement="outside"
                            placeholder="Max Injection Rate"
                            size="sm"
                            type="number"
                            min={0}
                            isRequired
                        />
                    </div>
                    <div className="self-end">
                        <Button
                            size="sm"
                            color="secondary"
                            startContent={<FontAwesomeIcon icon={faSave} />}
                            onPress={() => {
                                f.trigger();
                                f.handleSubmit(onSave)();
                            }}
                            isDisabled={savePump.isPending}
                            isLoading={savePump.isPending}
                        >
                            {t("save", {
                                ns: "common",
                            })}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
}

export default PumpForm;
